import React from 'react';
import styled from 'styled-components';
import { useWizardStep, WizardNavigationHook, SecondaryButton, StackPanel, useWizardActions, ActionButton, ProgressIndicator } from '@agilepoint/wat-react-ux';
import { useTranslate } from '@agilepoint/wat-react';
import { isFunction } from '@agilepoint/utils-common';
import { FooterBlock } from '../styled-components';
import { StyledPrimaryButton } from 'common';

export interface IWizardNavigationActionsProps {
    blockNext?: boolean;
    stepId: string;
    nextStepId?: string;
    prevStepId?: string;
    nextParams?: any;
    prevParams?: any;
    skippable?: boolean;
    showCancel?: boolean;
    primaryPositiveActionType?: 'build' | 'next' | 'save';
    secondaryPositiveActionType?: string;
    secondaryAction?: WizardNavigationHook;
    hook?: WizardNavigationHook;
    prevHook?: WizardNavigationHook;
    onCancel?(): Promise<void>;
}

const StyledProgressIndicator = styled(ProgressIndicator)`
    width: 100%;
`;

export const WizardNavigationActions = React.memo((props: IWizardNavigationActionsProps) => {
    const {
        stepId, nextStepId, prevStepId, nextParams, prevParams, onCancel, hook, secondaryAction,
        prevHook, skippable, primaryPositiveActionType, blockNext, secondaryPositiveActionType, showCancel = false
    } = props;
    const { isFirst, isLast, isNavigating, next, previous } = useWizardStep(stepId);
    const { navigate } = useWizardActions();

    const positiveActionLabel = React.useMemo(() => primaryPositiveActionType ?? (isLast ? 'build' : 'next'), [isLast, primaryPositiveActionType]);
    const translate = useTranslate();

    const onPositiveActionClick = React.useCallback(() => {
        const navArgs = { beforeNavigate: hook, params: isFunction(nextParams) ? nextParams() : nextParams };
        if (nextStepId) {
            navigate(nextStepId, navArgs);
        } else {
            next(navArgs);
        }
    }, [nextStepId, navigate, hook, nextParams, next]);

    const onPreviousClick = React.useCallback(() => {
        const navArgs = { beforeNavigate: prevHook, params: isFunction(prevParams) ? prevParams() : prevParams };
        if (prevStepId) {
            navigate(prevStepId, navArgs);
        } else {
            previous(navArgs);
        }
    }, [prevStepId, navigate, prevHook, prevParams, previous]);

    const onCancelClick = React.useCallback(async (e: React.MouseEvent) => {
        e.preventDefault();
        if (onCancel) {
            await onCancel();
        }
    }, [onCancel]);

    const onSkipClick = React.useCallback(() => {
        if (nextStepId) {
            navigate(nextStepId);
        } else {
            next();
        }
    }, [next, navigate, nextStepId]);

    const onSecondaryPositiveActionClick = React.useCallback(async () => {
        const navArgs = { beforeNavigate: secondaryAction, params: isFunction(nextParams) ? nextParams() : nextParams };
        if (nextStepId) {
            navigate(nextStepId, navArgs);
        } else {
            next(navArgs);
        }
    }, [navigate, next, nextParams, nextStepId, secondaryAction]);

    const renderCancelButton = () => (<ActionButton hidden={isFirst} label={translate('common.actions.cancel')} onClick={onCancelClick} />);

    const renderSecondaryPositiveActionTypeButton = () => (<StyledPrimaryButton type="submit" disabled={blockNext || isNavigating} onClick={onSecondaryPositiveActionClick} label={secondaryPositiveActionType} />);

    return (
        <FooterBlock width="100%" height="60px">
            {blockNext || isNavigating
                ? <StyledProgressIndicator progressIndicatorType='indeterminate' />
                :
                (
                    <StackPanel alignment="center" width="100%" gap="25" justifyContent="flex-end">
                        {showCancel ? renderCancelButton() : <></>}
                        <SecondaryButton hidden={!skippable} type="submit" onClick={onSkipClick}
                            label={translate('common.actions.skip')} />
                        <SecondaryButton type="submit" disabled={isNavigating} onClick={onPreviousClick}
                            hidden={isFirst} label={translate('common.actions.back')} />
                        {secondaryPositiveActionType ? renderSecondaryPositiveActionTypeButton() : <></>}
                        <StyledPrimaryButton
                            type="submit"
                            disabled={blockNext || isNavigating}
                            onClick={onPositiveActionClick}
                            label={translate(`common.actions.${positiveActionLabel}`)} />
                    </StackPanel>
                )
            }
        </FooterBlock>
    );
});
